// Public Talk URLs — admin page for creating/managing public click-to-call links.
// Each link is signed-token + per-link rate-limited + Turnstile-protected
// by default.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Copy, RefreshCw, Globe, X, Shield, Key, Eye, EyeOff } from 'lucide-react';
import { api } from '../lib/api';

export default function PublicTalk() {
  const me = api.me();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any | null>(null);
  const [showKeysFor, setShowKeysFor] = useState<any | null>(null);

  const { data: links = [] } = useQuery({
    queryKey: ['website-links', me?.domain_uuid],
    queryFn: () => me?.domain_uuid ? api.websiteLinks.list(me.domain_uuid) : Promise.resolve([]),
    enabled: !!me?.domain_uuid,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.websiteLinks.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-links', me?.domain_uuid] }),
  });
  const rotate = useMutation({
    mutationFn: (id: string) => api.websiteLinks.rotateSlug(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-links', me?.domain_uuid] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Public Talk URLs</h1>
        <button className="btn-primary" onClick={() => setEditing({ pv_dn: '', pv_chat_enabled: true, pv_calls_enabled: true, require_signed_token: true, rate_limit_per_minute: 10, require_turnstile: true })}>
          <Plus size={16} className="inline mr-1" /> Add
        </button>
      </div>

      <div className="card p-3 text-sm flex items-start gap-2">
        <Shield size={16} className="text-success mt-0.5 shrink-0" />
        <div>
          <strong>Stratus security defaults</strong> — every public link requires a signed
          token (10-min TTL) and is rate-limited per IP/minute. Turnstile is on by default.
          You can relax these per link if you're embedding in a trusted partner site.
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>DN</th><th>Slug</th><th>Channels</th><th>Limits</th><th>Hidden</th><th></th></tr></thead>
          <tbody>
            {links.map((l: any) => (
              <tr key={l.link_uuid} className="hover:bg-elevated">
                <td className="font-mono">{l.pv_dn}</td>
                <td className="font-mono text-xs">
                  /talk/{l.slug}
                  <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/talk/${l.slug}`)} className="btn-ghost p-1 ml-1"><Copy size={12} /></button>
                  <button onClick={() => rotate.mutate(l.link_uuid)} className="btn-ghost p-1" title="Rotate slug"><RefreshCw size={12} /></button>
                </td>
                <td>
                  {l.pv_calls_enabled && <span className="badge mr-1">call</span>}
                  {l.pv_chat_enabled && <span className="badge">chat</span>}
                  {l.pv_def_record && <span className="badge bg-warning/20 text-warning ml-1">rec</span>}
                </td>
                <td className="text-xs">
                  {l.require_signed_token && <span className="badge bg-success/20 text-success mr-1">signed</span>}
                  {l.require_turnstile && <span className="badge bg-success/20 text-success mr-1">turnstile</span>}
                  <span className="text-textSecondary">{l.rate_limit_per_minute}/min</span>
                </td>
                <td>{l.pv_hidden ? '🔒' : ''}</td>
                <td>
                  <button className="btn-ghost p-1" onClick={() => setEditing(l)}>Edit</button>
                  <button className="btn-ghost p-1" title="Embed keys" onClick={() => setShowKeysFor(l)}><Key size={14} /></button>
                  <button className="btn-ghost p-1 text-danger" onClick={() => confirm('Delete?') && remove.mutate(l.link_uuid)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {links.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">No public links yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && me?.domain_uuid && <EditModal domain_uuid={me.domain_uuid} link={editing} onClose={() => setEditing(null)} />}
      {showKeysFor && <EmbedKeysModal link={showKeysFor} onClose={() => setShowKeysFor(null)} />}
    </div>
  );
}

function EmbedKeysModal({ link, onClose }: { link: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [origins, setOrigins] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);

  const { data: keys = [] } = useQuery({
    queryKey: ['embed-keys', link.link_uuid],
    queryFn: () => api.embedKeys.list(link.link_uuid),
  });

  const create = useMutation({
    mutationFn: () => api.embedKeys.create({
      link_uuid: link.link_uuid,
      label: label || null,
      allowed_origins: origins ? origins.split(',').map(s => s.trim()).filter(Boolean) : null,
    }),
    onSuccess: (r: any) => {
      setRevealed(r.embed_key);
      setLabel('');
      setOrigins('');
      qc.invalidateQueries({ queryKey: ['embed-keys', link.link_uuid] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.embedKeys.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['embed-keys', link.link_uuid] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.embedKeys.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['embed-keys', link.link_uuid] }),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Key size={18} /> Embed keys for /talk/{link.slug}</h2>
          <button className="btn-ghost p-1" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="card p-3 text-sm bg-elevated">
          Embed keys let partner sites mint short-lived signed tokens via
          <code className="bg-base px-1 mx-1 rounded">POST /api/talk-v2/{link.slug}/mint</code>
          with the <code className="bg-base px-1 rounded">X-Embed-Key</code> header.
          Tokens then go in the public-talk URL as <code className="bg-base px-1 rounded">?t=…</code>.
        </div>

        {revealed && (
          <div className="card p-3 bg-warning/10 border border-warning text-sm space-y-2">
            <div className="font-semibold">⚠ Copy this key now — it's shown only once.</div>
            <div className="font-mono break-all bg-base p-2 rounded flex items-center gap-2">
              <span className="flex-1">{revealed}</span>
              <button className="btn-ghost p-1" onClick={() => navigator.clipboard.writeText(revealed)}><Copy size={14} /></button>
              <button className="btn-ghost p-1" onClick={() => setRevealed(null)}><X size={14} /></button>
            </div>
          </div>
        )}

        <div className="card p-3 space-y-2">
          <h3 className="font-semibold text-sm">Issue new key</h3>
          <div className="grid grid-cols-2 gap-2">
            <input className="input" placeholder="Label (e.g. acme.com prod)" value={label} onChange={e => setLabel(e.target.value)} />
            <input className="input" placeholder="Allowed origins (comma-sep, blank=any)" value={origins} onChange={e => setOrigins(e.target.value)} />
          </div>
          <button className="btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>
            <Plus size={14} className="inline mr-1" />Issue
          </button>
        </div>

        <div className="card p-0 overflow-hidden">
          <table className="table">
            <thead><tr><th>Label</th><th>Key</th><th>Origins</th><th>Last used</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {keys.map((k: any) => (
                <tr key={k.embed_uuid}>
                  <td>{k.label || '—'}</td>
                  <td className="font-mono text-xs">{k.embed_key_preview}</td>
                  <td className="text-xs">{(k.allowed_origins || []).join(', ') || <span className="text-textSecondary">any</span>}</td>
                  <td className="text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}</td>
                  <td>{k.revoked_at ? <span className="badge bg-danger/20 text-danger">revoked</span> : <span className="badge bg-success/20 text-success">active</span>}</td>
                  <td>
                    {!k.revoked_at && <button className="btn-ghost p-1" title="Revoke" onClick={() => confirm('Revoke?') && revoke.mutate(k.embed_uuid)}><EyeOff size={14} /></button>}
                    <button className="btn-ghost p-1 text-danger" onClick={() => confirm('Delete?') && remove.mutate(k.embed_uuid)}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {keys.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-textSecondary">No keys issued yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EditModal({ domain_uuid, link, onClose }: { domain_uuid: string; link: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(link);

  const save = useMutation({
    mutationFn: () => link.link_uuid
      ? api.websiteLinks.update(link.link_uuid, form)
      : api.websiteLinks.create({ ...form, domain_uuid }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-links', domain_uuid] });
      onClose();
    },
  });

  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Globe size={18} />{link.link_uuid ? 'Edit' : 'New'} Public Link</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        <Field label="Destination DN" hint="The extension/queue/IVR this link routes to">
          <input className="input font-mono" value={form.pv_dn || ''} onChange={e => set('pv_dn', e.target.value)} placeholder="e.g. 1000 or queue-sales" />
        </Field>

        <div className="space-y-2">
          <Toggle label="Allow voice calls" v={form.pv_calls_enabled} on={v => set('pv_calls_enabled', v)} />
          <Toggle label="Allow chat" v={form.pv_chat_enabled} on={v => set('pv_chat_enabled', v)} />
          <Toggle label="Auto-record voice calls" v={form.pv_def_record} on={v => set('pv_def_record', v)} />
          <Toggle label="Hidden (don't list publicly)" v={form.pv_hidden} on={v => set('pv_hidden', v)} />
        </div>

        <div className="border-t border-border pt-3 space-y-2">
          <h3 className="font-semibold text-sm">Security</h3>
          <Toggle label="Require signed token (HMAC + 10-min TTL)" v={form.require_signed_token} on={v => set('require_signed_token', v)} />
          <Toggle label="Require Cloudflare Turnstile" v={form.require_turnstile} on={v => set('require_turnstile', v)} />
          <Field label="Rate limit per minute (per IP)">
            <input className="input" type="number" min={1} max={1000} value={form.rate_limit_per_minute || 10}
                   onChange={e => set('rate_limit_per_minute', parseInt(e.target.value, 10) || 10)} />
          </Field>
        </div>

        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!form.pv_dn || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <div className="text-xs text-textSecondary">{hint}</div>}
    </div>
  );
}
function Toggle({ label, v, on }: { label: string; v: boolean; on: (v: boolean) => void }) {
  return <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!v} onChange={e => on(e.target.checked)} /> {label}</label>;
}
